import { useState, useEffect } from "react";

import { localizePath } from "@/routes/localizedPath";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Link } from "react-router-dom";
import { Shield, Cookie } from "lucide-react";

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

const DEFAULT_PREFERENCES: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
  functional: false,
};

const ALL_ACCEPTED: CookiePreferences = {
  necessary: true,
  analytics: true,
  marketing: true,
  functional: true,
};

const STORAGE_KEY = "cookie-preferences";

export function getCookiePreferences(): CookiePreferences | null {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored ? JSON.parse(stored) : null;
}

const CookieConsent = () => {
  const { t, lang } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(DEFAULT_PREFERENCES);

  useEffect(() => {
    const stored = getCookiePreferences();
    if (!stored) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    setVisible(false);
    setSettingsOpen(false);
    // Notify same-tab listeners (e.g. analytics loaders) that prefs changed.
    try {
      window.dispatchEvent(new CustomEvent("cookie-consent-updated", { detail: prefs }));
    } catch {
      /* no-op */
    }
  };

  const handleAcceptAll = () => savePreferences(ALL_ACCEPTED);
  const handleAcceptNecessary = () => savePreferences(DEFAULT_PREFERENCES);
  const handleSaveCustom = () => savePreferences(preferences);

  if (!visible) return null;

  const texts = lang === "sl" ? {
    title: "Uporabljamo piškotke",
    description: "Ta spletna stran uporablja piškotke za zagotavljanje najboljše uporabniške izkušnje. Nekateri piškotki so nujni za delovanje strani, drugi nam pomagajo izboljšati naše storitve.",
    acceptAll: "Sprejmi vse",
    customize: "Prilagodi",
    acceptNecessary: "Samo nujni",
    privacyLink: "Preberi Splošne pogoje in Politiko zasebnosti",
    settingsTitle: "Nastavitve piškotkov",
    settingsDesc: "Izberite, katere kategorije piškotkov želite dovoliti. Nujni piškotki so vedno aktivni, saj so potrebni za delovanje spletne strani.",
    necessary: "Nujni piškotki",
    necessaryDesc: "Ti piškotki so nujni za osnovno delovanje spletne strani. Brez njih stran ne deluje pravilno. Vključujejo piškotke za shranjevanje soglasja za piškotke.",
    analytics: "Analitični piškotki",
    analyticsDesc: "Ti piškotki nam pomagajo razumeti, kako obiskovalci uporabljajo našo spletno stran. Vse informacije so anonimizirane.",
    marketing: "Marketinški piškotki",
    marketingDesc: "Ti piškotki se uporabljajo za sledenje obiskovalcem na spletnih straneh, da se jim prikazujejo ustrezni oglasi.",
    functional: "Funkcijski piškotki",
    functionalDesc: "Ti piškotki omogočajo izboljšane funkcionalnosti, kot so personalizacija vsebine in zapomnitev vaših nastavitev.",
    save: "Shrani nastavitve",
    alwaysActive: "Vedno aktivni",
  } : {
    title: "We use cookies",
    description: "This website uses cookies to ensure the best user experience. Some cookies are essential for the site to function, while others help us improve our services.",
    acceptAll: "Accept all",
    customize: "Customize",
    acceptNecessary: "Necessary only",
    privacyLink: "Read Terms & Privacy Policy",
    settingsTitle: "Cookie settings",
    settingsDesc: "Choose which cookie categories you want to allow. Necessary cookies are always active as they are required for the website to function.",
    necessary: "Necessary cookies",
    necessaryDesc: "These cookies are essential for the basic functioning of the website. Without them, the site does not work properly. They include cookies for storing cookie consent.",
    analytics: "Analytics cookies",
    analyticsDesc: "These cookies help us understand how visitors use our website. All information is anonymized.",
    marketing: "Marketing cookies",
    marketingDesc: "These cookies are used to track visitors across websites to display relevant advertisements.",
    functional: "Functional cookies",
    functionalDesc: "These cookies enable enhanced functionality such as content personalization and remembering your settings.",
    save: "Save settings",
    alwaysActive: "Always active",
  };

  return (
    <>
      {/* Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[100] animate-in slide-in-from-bottom duration-500">
        <div className="mx-auto max-w-4xl px-4 pb-4">
          <div className="rounded-xl border bg-background p-5 shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <Cookie className="h-6 w-6 text-primary mt-0.5 shrink-0" />
              <div>
                <h3 className="font-heading font-semibold text-foreground text-base mb-1">{texts.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{texts.description}</p>
                <Link 
                  to={localizePath("/splosni-pogoji", lang)} 
                  className="text-sm text-primary hover:underline mt-1 inline-flex items-center gap-1"
                >
                  <Shield className="h-3.5 w-3.5" />
                  {texts.privacyLink}
                </Link>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
              <Button variant="outline" size="sm" onClick={handleAcceptNecessary} className="text-xs">
                {texts.acceptNecessary}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)} className="text-xs">
                {texts.customize}
              </Button>
              <Button size="sm" onClick={handleAcceptAll} className="text-xs font-semibold px-6">
                {texts.acceptAll}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{texts.settingsTitle}</DialogTitle>
            <DialogDescription>{texts.settingsDesc}</DialogDescription>
          </DialogHeader>

          <div className="space-y-5 mt-2">
            {/* Necessary - always on */}
            <div className="flex items-start justify-between gap-4 p-3 rounded-lg bg-muted/50">
              <div className="flex-1">
                <p className="font-medium text-sm text-foreground">{texts.necessary}</p>
                <p className="text-xs text-muted-foreground mt-1">{texts.necessaryDesc}</p>
              </div>
              <span className="text-xs text-primary font-medium whitespace-nowrap mt-1">{texts.alwaysActive}</span>
            </div>

            {/* Analytics */}
            <div className="flex items-start justify-between gap-4 p-3 rounded-lg border">
              <div className="flex-1">
                <p className="font-medium text-sm text-foreground">{texts.analytics}</p>
                <p className="text-xs text-muted-foreground mt-1">{texts.analyticsDesc}</p>
              </div>
              <Switch
                checked={preferences.analytics}
                onCheckedChange={(v) => setPreferences({ ...preferences, analytics: v })}
              />
            </div>

            {/* Marketing */}
            <div className="flex items-start justify-between gap-4 p-3 rounded-lg border">
              <div className="flex-1">
                <p className="font-medium text-sm text-foreground">{texts.marketing}</p>
                <p className="text-xs text-muted-foreground mt-1">{texts.marketingDesc}</p>
              </div>
              <Switch
                checked={preferences.marketing}
                onCheckedChange={(v) => setPreferences({ ...preferences, marketing: v })}
              />
            </div>

            {/* Functional */}
            <div className="flex items-start justify-between gap-4 p-3 rounded-lg border">
              <div className="flex-1">
                <p className="font-medium text-sm text-foreground">{texts.functional}</p>
                <p className="text-xs text-muted-foreground mt-1">{texts.functionalDesc}</p>
              </div>
              <Switch
                checked={preferences.functional}
                onCheckedChange={(v) => setPreferences({ ...preferences, functional: v })}
              />
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <Button variant="outline" className="flex-1" onClick={handleAcceptNecessary}>
              {texts.acceptNecessary}
            </Button>
            <Button className="flex-1 font-semibold" onClick={handleSaveCustom}>
              {texts.save}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
